import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, AlertTriangle, ExternalLink, Info } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Notice } from "@/components/Badges";
import { DEV_STAGES } from "@/lib/format";

const STAGE_NAMES = ["Describe your project", "Check your options", "Prepare your information", "Review the application", "Complete and track"];
const STAGE_OF = { G1: 0, G2: 1, G3: 1, G4: 2, G5: 2, G6: 2, G7: 2, G8: 2, G9: 3, G10: 4, G11: 4, G12: 4, P1: 0, P2: 1, P3: 2, P4: 2, P5: 2, P6: 2, P7: 2, P8: 3, P9: 4, P10: 4, P11: 4, P12: 4 };

const INTAKE = [
  ["jurisdiction", "Where do you need funding or protection?", { uk: "UK only", uk_plus: "UK and other countries", outside: "Outside the UK" }],
  ["disclosure", "Has the innovation been disclosed outside the business?", { none: "No disclosure", confidential_only: "Only under NDA / confidentiality", public: "Yes, publicly" }],
  ["stage", "Project stage", DEV_STAGES],
  ["ownership", "Who owns the innovation?", { business: "The business owns all of it", founder: "A founder or individual owns it", shared: "Shared with contractors, partners or a university", unclear: "Not sure" }],
];

const Intake = ({ j, save }) => {
  const [v, setV] = useState(j.intake);
  return (
    <section className="card space-y-4 p-6" data-testid="journey-intake">
      <h2 className="font-semibold text-[#0B192C]">Before you start</h2>
      <p className="text-sm text-slate-600">Both journeys start with these four questions.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {INTAKE.map(([k, l, o]) => (
          <div key={k}><label className="label" htmlFor={`in-${k}`}>{l}</label>
            <select id={`in-${k}`} data-testid={`intake-${k}`} className="input" value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })}>
              <option value="">Select…</option>{Object.entries(o).map(([a, b]) => <option key={a} value={a}>{b}</option>)}
            </select>
          </div>
        ))}
        {v.disclosure === "public" && <div><label className="label" htmlFor="in-dd">First public disclosure date</label><input id="in-dd" type="date" data-testid="intake-disclosure-date" className="input" value={v.disclosure_date || ""} onChange={(e) => setV({ ...v, disclosure_date: e.target.value })} /></div>}
      </div>
      <button onClick={() => save({ intake: v })} data-testid="intake-save-button" className="btn-teal btn-sm">Save and continue</button>
    </section>
  );
};

const StepView = ({ j, step, save, goTab }) => {
  const navigate = useNavigate();
  const state = j.steps?.[step.key] || {};
  const [items, setItems] = useState(state.items || []);
  const [notes, setNotes] = useState(state.notes || "");
  useEffect(() => {
    setItems(state.items || []);
    setNotes(state.notes || "");
  }, [step.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const idx = j.definition.findIndex((s) => s.key === step.key);
  const next = j.definition[idx + 1];
  const open = () => (step.link === "search" ? navigate("/app/ip-search") : goTab(step.link));
  return (
    <section className="card p-6 sm:p-8" data-testid={`journey-step-${step.key}`}>
      <p className="caption">Step {step.key}</p>
      <h2 className="mt-1 text-xl font-bold text-[#0B192C]">{step.title}</h2>
      <p className="mt-3 text-sm leading-relaxed text-slate-700">{step.action}</p>
      {j.signals[step.key] && <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-900" data-testid="journey-signal"><Info className="h-3.5 w-3.5" />From your workspace: {j.signals[step.key]}</p>}
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div><p className="text-sm font-semibold">Evidence you'll need</p><ul className="mt-1 list-disc pl-5 text-sm text-slate-600">{step.evidence.map((e) => <li key={e}>{e}</li>)}</ul></div>
        <div><p className="text-sm font-semibold">What to produce</p><p className="mt-1 text-sm text-slate-600">{step.produce}</p></div>
      </div>
      <div className="mt-5 rounded-xl bg-slate-50 p-4">
        <p className="text-sm font-semibold">Completion check</p>
        <p className="mt-1 text-xs text-slate-600">{step.check}</p>
        <div className="mt-3 space-y-2">
          {step.items.map((it, i) => (
            <label key={it} className="flex items-center gap-3 text-sm"><input type="checkbox" data-testid={`journey-item-${step.key}-${i}`} checked={items.includes(it)} onChange={(e) => setItems(e.target.checked ? [...items, it] : items.filter((x) => x !== it))} className="h-4 w-4 accent-[#0D9488]" />{it}</label>
          ))}
        </div>
      </div>
      <label className="label mt-5" htmlFor="j-notes">Your notes</label>
      <textarea id="j-notes" rows={3} data-testid="journey-notes" className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
      {step.refs.length > 0 && <div className="mt-3 flex flex-wrap gap-3">{step.refs.map((r) => <a key={r} href={r} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#0D9488] hover:underline">Official guidance <ExternalLink className="h-3 w-3" /></a>)}</div>}
      <div className="mt-6 flex flex-wrap gap-2">
        <button onClick={() => save({ step_key: step.key, items, notes })} data-testid="journey-save-button" className="btn-ghost btn-sm">Save and return later</button>
        {!state.completed ? (
          <button onClick={() => save({ step_key: step.key, items, notes, completed: true, current: next?.key || step.key })} data-testid="journey-complete-button" className="btn-teal btn-sm">Mark step complete{next ? ` · next: ${next.key}` : ""}</button>
        ) : (
          <button onClick={() => save({ step_key: step.key, completed: false })} data-testid="journey-reopen-button" className="btn-ghost btn-sm">Reopen step</button>
        )}
        {step.link && <button onClick={open} data-testid="journey-open-tool" className="btn-ghost btn-sm">Open the related tool</button>}
      </div>
    </section>
  );
};

export default function JourneyPanel({ projectId, kind, goTab }) {
  const [j, setJ] = useState(null);
  const [cur, setCur] = useState(null);
  const url = `/projects/${projectId}/journeys/${kind}`;
  useEffect(() => {
    api.get(url).then(({ data }) => { setJ(data); setCur(data.current); });
  }, [url]);
  const save = async (body) => {
    try {
      const { data } = await api.put(url, body);
      setJ(data);
      if (body.current) setCur(body.current);
      if (Object.keys(body).some((k) => k !== "current")) toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!j) return <p className="text-sm text-slate-500">Loading…</p>;
  const pct = Math.round((j.completed_count / j.total) * 100);
  const step = j.definition.find((s) => s.key === cur) || j.definition[0];
  return (
    <div className="space-y-5" data-testid={`journey-${kind}`}>
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div><h2 className="text-xl font-bold text-[#0B192C]">{kind === "grant" ? "Apply for a Business Grant" : "Prepare a Patent Application"}</h2><p className="text-xs text-slate-500">Based on: {j.source}</p></div>
        <div className="sm:w-64"><div className="flex justify-between text-xs"><span>Preparation completeness</span><b data-testid="journey-completeness">{pct}%</b></div><div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-[#56BEBF] transition-[width]" style={{ width: `${pct}%` }} /></div><p className="mt-1 text-[11px] text-slate-500">{j.completeness_note}</p></div>
      </div>
      {j.flags.map((f) => <Notice key={f} tone="warn" testid="journey-flag"><AlertTriangle className="mr-1 inline h-4 w-4" />{f}{kind === "patent" && " Flagged for review, not a legal opinion."}</Notice>)}
      {!j.intake_complete ? <Intake j={j} save={save} /> : (
        <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
          <ol className="card h-fit space-y-1 p-3" aria-label="Steps">
            {j.definition.map((s, i) => {
              const done = j.steps?.[s.key]?.completed;
              const stg = STAGE_OF[s.key];
              const head = i === 0 || STAGE_OF[j.definition[i - 1].key] !== stg;
              return (
                <li key={s.key}>{head && <p className="caption px-2.5 pb-1 pt-2 text-[#0D9488]" data-testid={`journey-stage-${stg}`}>{stg + 1}. {STAGE_NAMES[stg]}</p>}<button onClick={() => { setCur(s.key); save({ current: s.key }); }} data-testid={`journey-nav-${s.key}`} className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs ${s.key === step.key ? "bg-teal-50 font-semibold text-teal-800" : "hover:bg-slate-50"}`}>
                  <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] ${done ? "bg-[#56BEBF] text-white" : "border border-slate-300 text-slate-500"}`}>{done ? <Check className="h-3 w-3" /> : s.key.slice(1)}</span>{s.title}
                </button></li>
              );
            })}
            <li className="pt-2"><button onClick={() => setJ({ ...j, intake_complete: false })} className="px-2.5 text-xs text-[#0D9488] hover:underline" data-testid="journey-edit-intake">Edit starting questions</button></li>
          </ol>
          <StepView j={j} step={step} save={save} goTab={goTab} />
        </div>
      )}
    </div>
  );
}
